import React, { useEffect } from "react";
import { useLocation, useRoutes } from "react-router-dom";
import { routes } from "./routes.js";
import ScrollToTopOnNavigate from "./components/ScrollToTopOnNavigate.jsx";
import { CookieConsentProvider } from "./context/CookieConsentContext.jsx";
import CookieBanner from "./components/CookieBanner.jsx";
import usePageTitle from "./hooks/usePageTitle.js";
import SalesLogo from "./assets/Images/logo-sales.png";
import MedicalLogo from "./assets/Images/logo-medical.png";

function applyFavicon(logoSrc) {
  let favicon = document.querySelector("link[rel='icon']");
  if (!favicon) {
    favicon = document.createElement("link");
    favicon.setAttribute("rel", "icon");
    document.head.appendChild(favicon);
  }

  const img = new Image();
  img.onload = () => {
    const size = 64;
    const padding = 6;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      favicon.setAttribute("href", logoSrc);
      return;
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    // Draw a rounded white tile for better contrast in browser tabs.
    const radius = 12;
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.moveTo(radius, 0);
    ctx.lineTo(size - radius, 0);
    ctx.quadraticCurveTo(size, 0, size, radius);
    ctx.lineTo(size, size - radius);
    ctx.quadraticCurveTo(size, size, size - radius, size);
    ctx.lineTo(radius, size);
    ctx.quadraticCurveTo(0, size, 0, size - radius);
    ctx.lineTo(0, radius);
    ctx.quadraticCurveTo(0, 0, radius, 0);
    ctx.closePath();
    ctx.fill();

    // If logo is wide (wordmark), use the left emblem area for a clearer favicon.
    const isWideWordmark = img.width > img.height * 1.4;
    const srcSize = isWideWordmark ? img.height : Math.min(img.width, img.height);
    const sx = isWideWordmark ? 0 : (img.width - srcSize) / 2;
    const sy = (img.height - srcSize) / 2;

    const drawSize = size - padding * 2;
    ctx.drawImage(img, sx, sy, srcSize, srcSize, padding, padding, drawSize, drawSize);

    favicon.setAttribute("href", canvas.toDataURL("image/png"));
  };
  img.onerror = () => {
    favicon.setAttribute("href", logoSrc);
  };
  img.src = logoSrc;
}

export default function App() {
  usePageTitle();
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname || "/";
    if (path.startsWith("/sales")) {
      applyFavicon(SalesLogo);
      return;
    }
    if (path.startsWith("/medical")) {
      applyFavicon(MedicalLogo);
      return;
    }
    // Homepage and any other route use one default REPEXO logo.
    applyFavicon(MedicalLogo);
  }, [location.pathname]);

  return (
    <CookieConsentProvider>
      <ScrollToTopOnNavigate />
      {useRoutes(routes)}
      <CookieBanner />
    </CookieConsentProvider>
  );
}